import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Router} from '@angular/router';
import {EstadoOnboardingPainel, OnboardingPainelService, ProdutoOnboarding} from '../onboarding-painel.service';
import {OnbFocoService} from '../apoio/onb-foco.service';
import {ImportadorIfoodService} from '../../services/importador-ifood.service';
import {busqueProdutosDoLink, detectePlataforma, extraiaProdutos, montePayload, NOMES_DAS_PLATAFORMAS, PlataformaDoLink} from './importacao-por-link';

interface GrupoDeProdutos { categoria: string; produtos: ProdutoNaRevisao[]; }
interface ProdutoNaRevisao extends ProdutoOnboarding { precoTexto: string; salvando?: boolean; salvo?: boolean; erro?: string; }

/**
 * Passo 1 · Cardápio. Três portas (foto/PDF/texto pela IA, link de outra plataforma, cadastro na mão), todas dentro
 * do passo, e a revisão de preços: produto sem preço não aparece na loja, então o passo só fecha com ao menos um
 * produto com preço. O link não abre o importador do catálogo: cola, reconhece a plataforma e traz tudo.
 */
@Component({
  selector: 'app-passo-cardapio',
  templateUrl: './passo-cardapio.component.html',
  styleUrls: ['./passo-cardapio.component.scss']
})
export class PassoCardapioComponent implements OnInit {
  @Input() estado: EstadoOnboardingPainel;
  @Output() concluido = new EventEmitter<void>();
  @Output() ajuda = new EventEmitter<{motivo: string; passo: string}>();

  modo: 'escolher' | 'importando' | 'link' | 'manual' | 'revisar' = 'escolher';
  carregando = false;
  erro = '';
  grupos: GrupoDeProdutos[] = [];

  readonly plataformasAceitas = NOMES_DAS_PLATAFORMAS.join(', ');
  link = '';
  trazendo = false;
  etapaDoLink = '';
  totalDoLink = 0;
  salvosDoLink = 0;
  falhasDoLink = 0;
  erroDoLink = '';
  semAdicionais = false;

  manual = {nome: '', preco: '', categoria: ''};
  adicionados: {nome: string; preco: number}[] = [];
  salvandoManual = false;
  erroManual = '';

  constructor(private onboarding: OnboardingPainelService, private router: Router, private foco: OnbFocoService,
              private importador: ImportadorIfoodService) {}

  ngOnInit(): void {
    const passo = this.estado?.passos?.find(p => p.id === 'cardapio');
    if (passo?.iniciado) this.carregueRevisao();
  }

  get produtos(): ProdutoNaRevisao[] { return this.grupos.reduce((lista, g) => lista.concat(g.produtos), [] as ProdutoNaRevisao[]); }

  get comPreco(): number { return this.produtos.filter(p => p.tamanhos > 0 || (p.preco !== null && p.preco > 0)).length; }

  get semPreco(): number { return this.produtos.length - this.comPreco; }

  importeComIa(): void { this.modo = 'importando'; }

  importeDeOutraPlataforma(): void {
    this.modo = 'link';
    this.erroDoLink = '';
    setTimeout(() => document.getElementById('onb-link-loja')?.focus(), 0);
  }

  cadastreNaMao(): void {
    this.modo = 'manual';
    this.erroManual = '';
    if (!this.manual.categoria && this.grupos.length) this.manual.categoria = this.grupos[0].categoria;
    setTimeout(() => document.getElementById('onb-manual-nome')?.focus(), 0);
  }

  volteParaAsPortas(): void {
    if (this.trazendo || this.salvandoManual) return;
    if (this.adicionados.length) return this.carregueRevisao();
    this.modo = this.grupos.length ? 'revisar' : 'escolher';
  }

  get plataformaDoLink(): PlataformaDoLink | null { return detectePlataforma(this.link); }

  get categorias(): string[] { return this.grupos.map(g => g.categoria).filter(c => c !== 'Sem categoria'); }

  get progressoDoLink(): number { return this.totalDoLink ? Math.round(this.salvosDoLink / this.totalDoLink * 100) : 0; }

  /** Cola o link, reconhece a plataforma, lê o cardápio e grava produto a produto; no fim, a revisão de preços. */
  async trazerDoLink(): Promise<void> {
    if (this.trazendo) return;
    if (!this.link.trim()) return this.foco.mostre('onb-link-loja', 'Cole aqui o link da sua loja no iFood ou na plataforma que você usa hoje.');
    const plataforma = this.plataformaDoLink;
    if (!plataforma) {
      return this.foco.mostre('onb-link-loja', `Esse link não é de uma plataforma que conhecemos (${this.plataformasAceitas}). Confira o link ou use a foto do cardápio.`);
    }
    this.trazendo = true;
    this.erroDoLink = '';
    this.semAdicionais = false;
    this.totalDoLink = this.salvosDoLink = this.falhasDoLink = 0;
    this.etapaDoLink = `Lendo seu cardápio no ${plataforma.nome}…`;
    try {
      const resposta = await busqueProdutosDoLink(this.importador, plataforma.id, this.link);
      const produtos = extraiaProdutos(resposta);
      if (!produtos.length) {
        this.erroDoLink = `Não achamos produtos novos nesse link. Confira se é o link da sua loja no ${plataforma.nome}.`;
        return;
      }
      if (plataforma.id === 'ifood') await this.carregueAdicionaisIfood(produtos);
      this.totalDoLink = produtos.length;
      for (const produto of produtos) {
        this.etapaDoLink = `Salvando ${this.salvosDoLink + this.falhasDoLink + 1} de ${produtos.length}: ${produto.nome || 'produto'}`;
        try {
          await this.importador.importeProduto(montePayload(produto));
          this.salvosDoLink++;
        } catch (e) {
          this.falhasDoLink++;
        }
      }
      await this.importador.finalizouImportacao().catch(() => null);
      if (!this.salvosDoLink) {
        this.erroDoLink = 'Não conseguimos salvar os produtos desse link. Tente de novo ou use a foto do cardápio.';
        return;
      }
      this.carregueRevisao();
    } catch (e) {
      this.erroDoLink = OnboardingPainelService.mensagem(e, 'Não foi possível ler esse link. Confira se ele abre a sua loja e tente de novo.');
      this.onboarding.registre('IMPORTACAO_FALHOU', `link ${plataforma.id}: ${String(this.erroDoLink).slice(0, 160)}`);
    } finally {
      this.trazendo = false;
    }
  }

  /**
   * No iFood os adicionais vêm à parte, 20 produtos por consulta, com pausa entre elas. Se o iFood não responder,
   * segue sem adicionais: o dono completa depois em Produtos, e o cardápio não trava aqui.
   */
  private async carregueAdicionaisIfood(produtos: any[]): Promise<void> {
    const comAdicionais = produtos.filter(p => p?.needChoices && p.idIfood);
    const ids: string[] = Array.from(new Set(comAdicionais.map(p => String(p.idIfood))));
    const paginas = Math.ceil(ids.length / 20);
    for (let i = 0; i < ids.length; i += 20) {
      this.etapaDoLink = `Buscando os adicionais dos produtos (${i / 20 + 1} de ${paginas})…`;
      let resposta: any = null;
      for (let tentativa = 1; tentativa <= 3 && !resposta; tentativa++) {
        resposta = await this.importador.listeAdicionaisProdutosIfood(this.link.trim(), ids.slice(i, i + 20), -1).catch(() => null);
        if (!resposta && tentativa < 3) await new Promise(ok => setTimeout(ok, 5000));
      }
      if (!resposta) { this.semAdicionais = true; return; }
      for (const item of resposta.produtos || []) {
        comAdicionais.filter(p => String(p.idIfood) === String(item.idIfood)).forEach(p => p.camposAdicionais = item.camposAdicionais || []);
      }
      if (i + 20 < ids.length) await new Promise(ok => setTimeout(ok, 5000));
    }
  }

  /** Cadastro rápido: nome, preço e categoria. Foto, descrição e adicionais ficam para depois, em Produtos. */
  async adicioneNaMao(): Promise<void> {
    if (this.salvandoManual) return;
    const nome = this.manual.nome.trim();
    if (!nome) return this.foco.mostre('onb-manual-nome', 'Escreva o nome do produto, como aparece para o cliente.');
    const preco = Math.round(PassoCardapioComponent.leiaPreco(this.manual.preco) * 100) / 100;
    if (!(preco > 0)) return this.foco.mostre('onb-manual-preco', 'Informe o preço. Ex.: 24,90');
    const categoria = this.manual.categoria.trim();
    if (!categoria) return this.foco.mostre('onb-manual-categoria', 'Em que parte do cardápio ele fica? Ex.: Lanches, Bebidas.');
    this.salvandoManual = true;
    this.erroManual = '';
    try {
      await this.importador.importeProduto(montePayload({nome, preco, categoria: {nome: categoria}}));
      this.adicionados.unshift({nome, preco});
      this.manual.nome = '';
      this.manual.preco = '';
      setTimeout(() => document.getElementById('onb-manual-nome')?.focus(), 0);
    } catch (e) {
      this.erroManual = OnboardingPainelService.mensagem(e, 'Não foi possível salvar o produto. Tente de novo.');
    } finally {
      this.salvandoManual = false;
    }
  }

  aoImportar(resultado: any): void {
    if (resultado === undefined || resultado === null) {
      this.modo = this.grupos.length ? 'revisar' : 'escolher';
      return;
    }
    this.carregueRevisao();
  }

  carregueRevisao(): void {
    this.modo = 'revisar';
    this.carregando = true;
    this.erro = '';
    this.onboarding.produtos().then(lista => {
      const porCategoria = new Map<string, ProdutoNaRevisao[]>();
      for (const p of lista || []) {
        const categoria = p.categoria || 'Sem categoria';
        if (!porCategoria.has(categoria)) porCategoria.set(categoria, []);
        porCategoria.get(categoria).push({...p, precoTexto: p.preco ? p.preco.toFixed(2).replace('.', ',') : ''});
      }
      this.grupos = Array.from(porCategoria.entries()).map(([categoria, produtos]) => ({categoria, produtos}));
      if (!this.grupos.length) this.modo = 'escolher';
    }).catch(e => this.erro = OnboardingPainelService.mensagem(e, 'Não foi possível carregar seus produtos.'))
      .finally(() => this.carregando = false);
  }

  semPrecoNoProduto(p: ProdutoNaRevisao): boolean { return !p.tamanhos && !(p.preco > 0); }

  salvePreco(p: ProdutoNaRevisao): void {
    const valor = PassoCardapioComponent.leiaPreco(p.precoTexto);
    if (!p.precoTexto || !Number.isFinite(valor) || valor <= 0) {
      if (p.precoTexto) p.erro = 'Preço inválido';
      return;
    }
    if (p.preco === Math.round(valor * 100) / 100) return;
    p.salvando = true;
    p.erro = '';
    this.onboarding.salvePreco(p.id, Math.round(valor * 100) / 100).then(() => {
      p.preco = Math.round(valor * 100) / 100;
      p.precoTexto = p.preco.toFixed(2).replace('.', ',');
      p.salvo = true;
      setTimeout(() => p.salvo = false, 1600);
    }).catch(e => p.erro = OnboardingPainelService.mensagem(e, 'Não salvou'))
      .finally(() => p.salvando = false);
  }

  /** "12,50", "12.50" e "1.250,00" viram número. */
  static leiaPreco(texto: string): number {
    const limpo = String(texto || '').replace(/[^\d.,]/g, '');
    const normalizado = limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo;
    return Number(normalizado);
  }

  abraEditor(p: ProdutoNaRevisao): void { this.router.navigate(['/admin/produtos-v2', p.id]); }

  /** Sempre habilitado: se faltar algo, mostra onde tocar. */
  conclua(): void {
    if (this.modo === 'revisar' && this.comPreco > 0) return this.concluido.emit();
    if (this.modo === 'manual') {
      if (this.adicionados.length) return this.concluido.emit();
      return this.foco.mostre('onb-manual-nome', 'Cadastre pelo menos um produto com nome e preço.');
    }
    if (this.modo !== 'revisar' || !this.produtos.length) {
      return this.foco.mostre('onb-cardapio-portas', 'Comece por aqui: escolha como trazer seu cardápio. A foto do cardápio é o jeito mais rápido.');
    }
    const primeiro = this.produtos.find(p => this.semPrecoNoProduto(p));
    this.foco.mostre(primeiro ? 'onb-preco-' + primeiro.id : 'onb-cardapio-portas',
      'Preencha o preço de pelo menos um produto: sem preço ele não aparece na loja.');
  }
}
